import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy, faFile } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAiAssistant } from '../../AiAssistantContext';
import * as api from '../../api';
import { cleanPath } from '../../cleanPath';
import { IChatMessage } from '../../types';
import remarkBreaks from 'remark-breaks';
import Markdown from 'components/common/Markdown';
import ToolActivity from './ToolActivity';

interface IProps {
  message: IChatMessage;
  isLastInGroup?: boolean;
  projectId?: number;
}

// The chat shows each line of a message on its own line, as the assistant wrote it
const CHAT_PLUGINS = [remarkBreaks];

const IMAGE_EXT_RE = /\.(png|jpg|jpeg|gif|webp)$/i;
const isImagePath = (s: string) => IMAGE_EXT_RE.test(s);
const basename = (s: string) => s.split('/').pop() || s;

const HistoryImageThumb: React.FC<{ projectId: number; filePath: string }> = ({ projectId, filePath }) => {
  const { state, dispatch } = useAiAssistant();
  const cached = state.fileContents[filePath];
  const [src, setSrc] = useState<string | null>(cached ?? null);

  useEffect(() => {
    if (cached !== undefined) { setSrc(cached); return; }
    api.fetchFileContent(projectId, filePath)
      .then((res) => {
        dispatch({ type: 'SET_FILE_CONTENT', path: filePath, content: res.content });
        setSrc(res.content);
      })
      .catch(() => {});
  }, [cached, projectId, filePath]);

  if (!src) return null;
  return <AttachThumb src={src} />;
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (isToday) {
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const MessageBubble: React.FC<IProps> = ({ message, isLastInGroup = true, projectId }) => {
  const isUser = message.role === 'user';
  const activitiesMap = new Map((message.activities || []).map((a) => [a.toolUseId, a]));
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(message.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const renderAssistantContent = () => {
    if (message.contentBlocks && message.contentBlocks.length > 0) {
      return message.contentBlocks.map((block, i) => {
        const prev = message.contentBlocks![i - 1];
        const needsDividerBefore = i > 0 && prev && prev.type !== block.type;
        if (block.type === 'activity') {
          const activity = activitiesMap.get(block.toolUseId);
          if (!activity) return null;
          return (
            <React.Fragment key={block.toolUseId}>
              {needsDividerBefore && <Divider />}
              <ToolActivity activity={activity} />
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={`text-${i}`}>
            {needsDividerBefore && <Divider />}
            <MarkdownContent>
              <Markdown plugins={CHAT_PLUGINS}>{cleanPath(block.text)}</Markdown>
            </MarkdownContent>
          </React.Fragment>
        );
      });
    }
    // Fallback for history messages without contentBlocks
    const hasActivities = message.activities && message.activities.length > 0;
    return (
      <>
        {hasActivities && (
          <ActivitiesSection>
            {message.activities!.map((a) => (
              <ToolActivity key={a.toolUseId} activity={a} />
            ))}
          </ActivitiesSection>
        )}
        {hasActivities && message.content && <Divider />}
        {message.content && (
          <MarkdownContent>
            <Markdown plugins={CHAT_PLUGINS}>{cleanPath(message.content)}</Markdown>
          </MarkdownContent>
        )}
      </>
    );
  };

  return (
    <BubbleRow $isUser={isUser} $isLastInGroup={isLastInGroup}>
      <BubbleWrap>
        <Bubble $isUser={isUser} $isLastInGroup={isLastInGroup}>
          {isUser ? (
            <>
              {message.attachmentPreviews && message.attachmentPreviews.length > 0 && (
                <AttachmentThumbs>
                  {message.attachmentPreviews.map((src, i) =>
                    src.startsWith('data:image/') ? (
                      <AttachThumb key={i} src={src} />
                    ) : isImagePath(src) && projectId ? (
                      <HistoryImageThumb key={i} projectId={projectId} filePath={src} />
                    ) : (
                      <AttachFileChip key={i}>
                        <FontAwesomeIcon icon={faFile} />
                        <span>{basename(src)}</span>
                      </AttachFileChip>
                    )
                  )}
                </AttachmentThumbs>
              )}
              {message.content && <UserText>{message.content}</UserText>}
            </>
          ) : (
            renderAssistantContent()
          )}
        </Bubble>
        <ActionButtons $isUser={isUser}>
          {message.content && (
            <ActionBtn $isUser={isUser} onClick={handleCopy} title="Copy message">
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
            </ActionBtn>
          )}
        </ActionButtons>
      </BubbleWrap>
      {isLastInGroup && message.timestamp && (
        <Timestamp $isUser={isUser}>{formatTime(message.timestamp)}</Timestamp>
      )}
    </BubbleRow>
  );
};

export default MessageBubble;

const BubbleRow = styled.div<{ $isUser: boolean; $isLastInGroup: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$isUser ? 'flex-end' : 'flex-start')};
  margin-bottom: ${(p) => (p.$isLastInGroup ? '14px' : '3px')};
  &:hover > ${() => Timestamp} {
    opacity: 1;
  }
`;

const BubbleWrap = styled.div`
  position: relative;
  max-width: 80%;
  &:hover > ${() => ActionButtons} {
    opacity: 1;
  }
`;

const Bubble = styled.div<{ $isUser: boolean; $isLastInGroup: boolean }>`
  padding: 8px 12px;
  border-radius: ${(p) => {
    if (p.$isUser) {
      return p.$isLastInGroup ? '8px 8px 3px 8px' : '8px 3px 3px 8px';
    }
    return p.$isLastInGroup ? '8px 8px 8px 3px' : '3px 8px 8px 3px';
  }};
  background: ${(p) => (p.$isUser ? '#1976d2' : '#f5f5f5')};
  color: ${(p) => (p.$isUser ? '#fff' : '#333')};
  font-size: 14px;
  line-height: 1.5;
`;

const Timestamp = styled.div<{ $isUser: boolean }>`
  font-size: 10px;
  color: #bbb;
  margin-top: 2px;
  padding: 0 4px;
  opacity: 0;
  transition: opacity 0.15s;
`;

const ActionButtons = styled.div<{ $isUser: boolean }>`
  position: absolute;
  top: 4px;
  ${(p) => (p.$isUser ? 'left: -30px' : 'right: -30px')};
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s;
`;

const ActionBtn = styled.button<{ $isUser: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  &:hover {
    color: #555;
    background: #eee;
  }
`;

const UserText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`;

const AttachmentThumbs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

const AttachThumb = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
`;

const AttachFileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.2);
  font-size: 12px;
  max-width: 180px;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ActivitiesSection = styled.div`
  margin-bottom: 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 8px 0;
`;

const MarkdownContent = styled.div`
  & p {
    margin: 0 0 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  & code {
    background: #e8e8e8;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 13px;
  }
  & pre {
    background: #2d2d2d;
    color: #f8f8f2;
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
    & code {
      background: none;
      padding: 0;
      color: inherit;
    }
  }
  & h1,
  & h2,
  & h3 {
    margin: 12px 0 6px;
    &:first-child {
      margin-top: 0;
    }
  }
  & ul,
  & ol {
    margin: 4px 0;
    padding-left: 20px;
  }
  & blockquote {
    border-left: 3px solid #ccc;
    margin: 8px 0;
    padding-left: 12px;
    color: #666;
  }
  & table {
    border-collapse: collapse;
    margin: 8px 0;
    & th,
    & td {
      border: 1px solid #ddd;
      padding: 4px 8px;
      font-size: 13px;
    }
    & th {
      background: #f0f0f0;
    }
  }
  & hr {
    border: none;
    border-top: 2px solid #ddd;
    margin: 14px 0;
  }
  /* A panel block, as the Gestor shows it: grey strip, orange for a note block */
  & .cm-block {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-left: 6px solid #c9c9c9;
    border-radius: 4px;
    padding: 6px 10px;
    margin: 8px 0;
    &.note {
      border-left-color: #f5b74e;
      background: #fffaf0;
    }
  }
  /* Folded material: one click opens it */
  & details {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 6px 10px;
    margin: 8px 0;
    & > summary {
      cursor: pointer;
      & > p {
        display: inline;
        margin: 0;
      }
    }
    &[open] > summary {
      margin-bottom: 6px;
    }
  }
`;
